<template>
    <div>
        <b-alert
            show
            variant="warning"
            dismissible
            v-if="reflectionHasChanged && wordHunterHasEdit"
            @dismissed="dismissWordHunterAlert"
        >
            {{trans('courses.word-hunter.word_hunter_change_warning')}}
        </b-alert>
        <div class="my-3">
            <div class="my-3">
                <div class="d-flex w-100 justify-content-end" v-if="showFullScreenEditor"> 
                    <b-dropdown v-if="textContent" variant="link" toggle-class="text-decoration-none" no-caret right>
                        <template v-slot:button-content>
                            <b-icon
                                icon="pencil-square"
                                variant="dark"
                                :aria-labelledby="`question-${promptId}`"
                            />
                        </template>
                        <b-dropdown-item href="#" @click="showFseModal">{{trans('courses.edit')}}</b-dropdown-item>
                        <b-dropdown-item href="#" @click="showWhModal" v-if="wordHunterHasEdit">{{trans('courses.word-hunter.edit')}}</b-dropdown-item>
                    </b-dropdown>
                </div>

                <div class="mb-3">
                    <privacy-alert
                        v-if="prompt.has_word_hunter"
                        variant="warning"
                        button-size="sm"
                        :popup-title="trans('courses.golden_question_title')"
                        :popup-content="trans('courses.golden_question_content')"
                        :popup-href="{name: 'privacy-legend'}"
                        :popover-id="`block-${prompt.id}`"
                        v-bind:targetBlank="true"
                        pill
                    >
                        <b-icon
                            icon="star-fill"
                            variant="warning"
                            font-scale="0.98"
                            aria-hidden="true"
                        />
                        {{ trans('courses.golden_question') }}
                    </privacy-alert>
                </div>

                <h6 v-if="prompt.question" :id="`question-${promptId}`" :class="textContent ? 'pt-3' : ''">{{prompt.question}}</h6>
                <p v-if="prompt.help_text" class="mb-2 text-opacity">{{prompt.help_text}}</p>
                <full-screen-editor
                    v-if="showFullScreenEditor"
                    :block-id="blockId"
                    :block-type="blockType"
                    :prompt-id="promptId"
                    :question="prompt.question"
                    :text-content="userAnswers(promptId) && (userAnswers(promptId).length > 0) ? userAnswers(promptId)[0].value : ''"
                    :mood-content="getPromptMoodValue"
                    :key="`${promptId}-full-screen-editor`"
                    @submit="updatePrompt"
                    ref="fseModal"
                    :moodDisabled="prompt.has_word_hunter"
                />
                <full-screen-editor-inline
                    v-if="type === 'open' && isInModal"
                    :prompt-id="promptId"
                    :question="prompt.question"
                    :text-content="textContent"
                    :key="`${promptId}-inline-editor`"
                    @submit="updatePrompt"
                />
                <word-hunter
                    v-if="type === 'open' && prompt.has_word_hunter"
                    :block-id="blockId"
                    :block-type="blockType"
                    :prompt-id="promptId"
                    :topic-id="prompt.topic_list.id"
                    :text-content="textContent"
                    :mood-content="getPromptMoodValue"
                    :key="promptId"
                    @hasSelectedWords="wordHunterEditBtn"
                    ref="whModal"
                    :streamId="streamId"
                    :aleId="aleId"
                    :elementId="elementId"
                />
                <options-prompt-block
                    :question="prompt.question"
                    :answers="prompt.option"
                    :id="pageId"
                    :key="promptId"
                    v-if="type === 'radios'"
                    :currentOption="userAnswers(promptId) && (userAnswers(promptId).length > 0) ? Number(userAnswers(promptId)[0].value) : -1"
                    @optionSelected="updatePrompt"
                />
                <checkbox-prompt-block
                    :question="prompt.question"
                    :answers="prompt.option"
                    :id="pageId"
                    :key="promptId"
                    v-if="type === 'checkboxes'"
                    :user-answers="userAnswers(promptId) ? userAnswers(promptId) : []"
                    @optionSelected="updatePrompt"
                />
                <rating-prompt-block
                    :question="prompt.question"
                    :answers="prompt.option"
                    :id="pageId"
                    :key="promptId"
                    v-if="type === 'likert'"
                    :currentOption="userAnswers(promptId) && (userAnswers(promptId).length > 0) ? Number(userAnswers(promptId)[0].value) : -1"
                    @optionSelected="updatePrompt"
                />
            </div>
        </div>
    </div>
</template>

<script>
    import FullScreenEditor from "../../organisms/FullScreenEditor";
    import FullScreenEditorInline from "../../organisms/FullScreenEditorInline";
    import WordHunter from "../word-hunter/WordHunter";
    import OptionsPromptBlock from "./blocks/OptionsPromptBlock";
    import RatingPromptBlock from "./blocks/RatingPromptBlock";
    import CheckboxPromptBlock from "./blocks/CheckboxPromptBlock";
    import PrivacyAlert from "../../molecules/PrivacyAlert";
    import {mapActions, mapGetters, mapMutations} from "vuex";

    export default {
        name: "PromptContainer",
        components:{
            FullScreenEditor,
            FullScreenEditorInline,
            OptionsPromptBlock,
            RatingPromptBlock,
            CheckboxPromptBlock,
            WordHunter,
            PrivacyAlert
        },
        props:{
            blockId: Number,
            blockType: String,
            prompt: Object,
            promptId: String,
            pageId: String,
            type: String,
            isInModal: Boolean
        },
        data(){
            return{
                userPrompt: this.prompt,
                wordHunterHasEdit: false,
                reflectionHasChanged: false
            }
        },
        methods:{
            ...mapActions({
                submitActivity: 'streams/submitActivity'
            }),
            ...mapMutations({
                setPromptAnswers: 'streams/setPromptAnswers'
            }),
            async updatePrompt(data){
                let details;
                if(this.type === 'checkboxes') {
                    details = data.answers.map(v => ({key: "answer", value: v}))
                } else {
                    details = [
                        {
                            key: "answer",
                            value: data.answers
                        }
                    ]
                }
                
                let elementType = this.currentElementType;
                
                details.push({key: "question", value: this.prompt.question})
                details.push({key: "type", value: this.type})
                details.push({key: "stream_id", value: this.streamId})
                details.push({key: "ale_id", value: this.aleId})
                details.push({key: "localisation", value: this.currentElementLocale})
                details.push({key: "element_type", value: elementType})
                details.push({key: "element_id", value: this.elementId})
                details.push({key: "mood_id", value: this.moodValid(data.mood) ? data.mood : null })
                if (this.prompt.has_word_hunter) {
                    details.push({key: "has_word_hunter", value: this.prompt.has_word_hunter})
                }

                let activityData = {
                    main: {
                        action: "submitted",
                        objectId: Number(this.promptId),
                        objectType: "prompt"

                    },
                    details: details
                }

                try{
                    trackEvent('Prompts',{
                        "prompt-type": this.type,
                        "prompt-id": activityData.main.objectId,
                        "stream-id": this.currentStreamId ? this.currentStreamId : this.$route.params.id,
                        "answer": activityData['details'][0].value,
                        "ale-id": this.elementId,
                        "element-type": elementType,
                        "element-id" : this.elementId,
                        "mood-id": this.moodValid(data.mood) ? data.mood : null,
                        "mood-icon": this.moodValid(data.mood) ? this.moodsList.moods.find((m) => m.id === data.mood).icon: null,
                        "mood-name": this.moodValid(data.mood) ? this.moodsList.moods.find((m) => m.id === data.mood).name: null,
                        "question": this.prompt.question,
                        "word-hunter": this.prompt.has_word_hunter
                    })
                    await this.submitActivity({data : [activityData]});

                    if (this.prompt.has_word_hunter && !this.textContent) {
                        this.showWhModal()
                    }

                    this.setPromptAnswers(activityData);
                }catch(err){
                    console.log(err);
                }
            },
            moodValid(valueToCheck) {
                return [null, undefined, this.moodsList.notSelected].indexOf(valueToCheck)===-1;
            },
            showFseModal() {
                this.reflectionHasChanged = true;
                this.$refs.fseModal.showModal();
                trackEvent('editPrompt', {
                    promptId: this.promptId,
                    streamId: this.streamId,
                    aleId: this.aleId,
                    miuId: this.elementId
                });
            },
            showWhModal() {
                if (this.reflectionHasChanged) {
                    this.reflectionHasChanged = false
                }
                this.$refs.whModal.showModal();
                trackEvent('editWordhunter', {
                    promptId: this.promptId,
                    streamId: this.streamId,
                    aleId: this.aleId,
                    miuId: this.elementId
                });
            },
            wordHunterEditBtn(value) {
                this.wordHunterHasEdit = value
            },
            dismissWordHunterAlert() {
                this.reflectionHasChanged = false
            }
        },
        computed:{
            ...mapGetters({
                userAnswers: 'streams/getPromptAnswers',
                getPromptMood: 'streams/getPromptMood',
                currentElementType: 'streams/getCurrentElementType',
                moodsList: 'moodsAndReactions/getMoodsList',
                currentAleId: 'streams/getCurrentAleId',
                currentStreamId: 'streams/getCurrentStreamId', 
                currentElementId: 'streams/getCurrentElementId',
                currentElementLocale: 'streams/getCurrentElementLocale'
            }),
            getPromptMoodValue: function() {
                if(this.getPromptMood(this.promptId) && (this.getPromptMood(this.promptId).length > 0) && (this.getPromptMood(this.promptId)[0].value))
                    return Number(this.getPromptMood(this.promptId)[0].value);
                else null;
            },
            textContent: function() {
                return this.userAnswers(this.promptId) && (this.userAnswers(this.promptId).length > 0) ? this.userAnswers(this.promptId)[0].value : ''
            },
            showFullScreenEditor: function() {
                return this.type === 'open' && !this.isInModal
            },
            streamId: function() {
                return this.currentStreamId ? this.currentStreamId : this.$route.params.id;
            },
            aleId: function() {
                return this.currentAleId ? this.currentAleId : this.$route.params.aleId;
            },
            elementId: function() {
                return this.$route.params.elementId ? this.$route.params.elementId : this.currentElementId;
            }
        }
    }
</script>

<style scoped lang="scss">
    .dropdown {
        margin: -1rem -1rem 0 0;
    }
</style>